import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { APP_CONFIG } from '@mnz-core/injectors/app-configuration';
import { AccountType } from '@mnz-core/models/_enums';
import { IAppConfiguration } from '@mnz-core/models/app-configuration';
import { IResponse } from '@mnz-core/models/response';
import { HttpClientService } from '@mnz-core/services/http-client.service';
import * as authActions from '@mnz-modules/login/store/actions/auth.actions';
import { getAppBaseUrl } from '@mnz-shared/shared.constants';
import * as appActions from '@mnz-store/actions/app.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { ILogin } from '@mnz-modules/login/models/login';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { IRootState } from '@mnz-core/modules/store/models/root-state';
import { StartOauthResponse } from '@mnz-modules/login/models/start-oauth-response';
import { ISAMLResponse } from '@mnz-modules/login/models/saml-response';
import { IMessage } from '@mnz-core/models/message';

@Injectable()
export class AuthEffects {
  constructor(
    @Inject(APP_CONFIG) private readonly _config: IAppConfiguration,
    @Inject(DOCUMENT) private readonly _document: Document,
    private readonly _actions$: Actions,
    private readonly _http: HttpClientService,
    private readonly _router: Router,
    private readonly _store$: Store<IRootState>
  ) {}

  doSamlLogin = createEffect(() =>
    this._actions$.pipe(
      ofType(authActions.doSamlLogin),
      switchMap(({ samlRequest, credentials, captcha }) => {
        return this._http
          .post<IResponse<ISAMLResponse>>(
            `${this._config.url_api_base}/mnz-auth-api/saml/login`,
            {
              ...samlRequest,
              username: credentials.username,
              password: credentials.password,
              captcha
            }
          )
          .pipe(
            map(({ success, data, error }) => {
              if (success) {
                return authActions.samlLoginSuccess({ samlResponse: data });
              }

              let errorMessage: IMessage;
              if (error.code === 'INVALID_AUTHENTICATION') { // wrong auth
                errorMessage = {
                  label: 'LOGIN.AUTH.ERROR.LOGIN_EMAIL_LABEL',
                  body: 'LOGIN.AUTH.ERROR.LOGIN_BODY',
                  error
                }
              } else if (error.code === 'ZISSB010') { // user is blocked
                errorMessage = {
                  label: 'LOGIN.SERVER_ERROR.ZISSB010.LABEL',
                  body: 'LOGIN.SERVER_ERROR.ZISSB010.BODY',
                  error
                }
              } else { // something else went wrong
                errorMessage = {
                  label: 'LOGIN.SERVER_ERROR.AUTHENTICATE.LABEL',
                  body: 'LOGIN.SERVER_ERROR.AUTHENTICATE.BODY',
                };
              }

              return appActions.addMessage({
                message: errorMessage,
                action: authActions.authenticateFail({ message: null }),
              });
            }),
            catchError((errorResponse) => {
              const { status } = errorResponse;

              if (status == 429) { // too many requests -> rate-limited
                return of(
                  appActions.addMessage({
                    message: {
                      label: 'LOGIN.SERVER_ERROR.RATE_LIMIT.LABEL',
                      body: 'LOGIN.SERVER_ERROR.RATE_LIMIT.BODY',
                      error: errorResponse,
                    },
                    action: authActions.authenticateFail({ message: null }),
                  })
                );
              } else { // something else went wrong
                return of(
                  appActions.addMessage({
                    message: {
                      label: 'LOGIN.SERVER_ERROR.AUTHENTICATE.LABEL',
                      body: 'LOGIN.SERVER_ERROR.AUTHENTICATE.BODY',
                      error: errorResponse,
                    },
                    action: authActions.authenticateFail({ message: null }),
                  })
                );
              }
            })
          );
      })
    )
  );

  samlLoginSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(authActions.samlLoginSuccess),
        tap(({samlResponse}) => {
          const form = document.createElement("form");
          form.action = samlResponse.acsUrl;
          form.method = "POST";

          const samlResponseField = document.createElement("input");
          samlResponseField.type = "hidden";
          samlResponseField.name = "SAMLResponse";
          samlResponseField.value = samlResponse.samlResponse;
          form.appendChild(samlResponseField);

          const relayStateField = document.createElement("input");
          relayStateField.type = "hidden";
          relayStateField.name = "RelayState";
          relayStateField.value = samlResponse.relayState;
          form.appendChild(relayStateField);

          document.body.appendChild(form);
          form.submit();
        })
      ),
    { dispatch: false }
  );

  authenticate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(authActions.authenticate),
      switchMap(({ credentials, accountType, captcha }) => {
        return this._http
          .post<IResponse<ILogin>>(
            `${this._config.url_api_base}/mnz-auth-api/login`,
            {
              ...credentials,
              captcha,
            }
          )
          .pipe(
            map(({ success, data }) => {
              return success
                ? authActions.authenticateSuccess({ login: data })
                : authActions.authenticateFail({
                message: {
                  body: 'LOGIN.SERVER_ERROR.AUTHENTICATE_FAIL.BODY',
                  label: ''
                    },
                  });
            }),
            catchError((error) => {
              const { status, errorCode } = error;

              switch (status) {
                case 429:
                  return of(
                    appActions.addMessage({
                      message: {
                        label: 'LOGIN.SERVER_ERROR.RATE_LIMIT.LABEL',
                        body: 'LOGIN.SERVER_ERROR.RATE_LIMIT.BODY',
                        error,
                      },
                      action: authActions.authenticateFail({ message: null }),
                    })
                  );
                  break;
                case 401:
                  return of(
                    appActions.addMessage({
                      message: {
                        label:
                          accountType === AccountType.CARD_HOLDER
                            ? 'LOGIN.AUTH.ERROR.LOGIN_EMAIL_LABEL'
                            : 'LOGIN.AUTH.ERROR.LOGIN_USERNAME_LABEL',
                        body: 'LOGIN.AUTH.ERROR.LOGIN_BODY',
                      },
                      action: authActions.authenticateFail({ message: null }),
                    })
                  );
                  break;
                case 403:
                  return of(
                    appActions.addMessage({
                      message: {
                        label: 'LOGIN.SERVER_ERROR.ZISSB010.LABEL',
                        body: 'LOGIN.SERVER_ERROR.ZISSB010.BODY',
                        error,
                      },
                      action: authActions.authenticateFail({ message: null }),
                    })
                  );
                  break;
              }

              if (errorCode) {
                return of(
                  appActions.addMessage({
                    message: {
                      label: `LOGIN.SERVER_ERROR.${errorCode}.LABEL`,
                      body: `LOGIN.SERVER_ERROR.${errorCode}.BODY`,
                      error,
                    },
                    action: authActions.authenticateFail({ message: null }),
                  })
                );
              }
              return of(
                appActions.addMessage({
                  message: {
                    label: 'LOGIN.SERVER_ERROR.AUTHENTICATE.LABEL',
                    body: 'LOGIN.SERVER_ERROR.AUTHENTICATE.BODY',
                    error,
                  },
                  action: authActions.authenticateFail({ message: null }),
                })
              );
            })
          );
      })
    )
  );

  authenticateSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(authActions.authenticateSuccess),
        filter(() => environment.production || environment.redirectAfterLogin),
        tap((login) => {
          this._document.cookie = `oauth_token=${window.btoa(login.login.bearerToken)};max-age=30;`;

          let path = getAppBaseUrl(this._config.contextPath) + '/home';

          const { language } = this._config.relay42_config;
          path += '?taal=' + language;

          location.assign(path);
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this._actions$.pipe(
      ofType(authActions.logout),
      switchMap(() => {
        return this._http.post<IResponse<any>>(`${this._config.url_api_base}/mnz-auth-api/logout`).pipe(
          map(() => {
            return authActions.logoutSuccess();
          }),
          catchError((error) => {
            return of(authActions.logoutFail({ message: null }));
          })
        );
      })
    )
  );

  logoutSuccess$ = createEffect(() => this._actions$.pipe(ofType(authActions.logoutSuccess)), { dispatch: false });
}
